/**
 * Main Library / Playlists, at the top of the library.
 *
 * Playlists are reachable as "Library → Playlists" and the Library must not become
 * cluttered. So playlists are a second view of the same screen rather than a second
 * screen, and NOT a sidebar row of their own — the sidebar has a Library entry, and
 * adding a third would make it the place you choose between two things that are one
 * thing. (The Library and the Studio DO have separate rows: those are two places, and
 * one row serving both was the navigation defect that split them.)
 *
 * The two views are separate ROUTES rather than a `useState` tab, which is what makes
 * Back work: open a playlist, press Back, and you return to the playlist list you
 * were looking at instead of to the track list you were not. It also makes a playlist
 * view linkable, which is the same reason every other screen has a URL.
 *
 * The existing TabBar primitive draws it, unchanged — same pills, same selected
 * colours, same aria-selected. This component only maps a tab to a path.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { TabBar } from '../ui';
import { paths } from '../../routes/paths';

/** Which of the two views is showing. */
export type LibraryView = 'tracks' | 'playlists';

/*
  "Main Library" rather than "Tracks": the tab strip is now the Library's own contents
  list, so the first tab names the library itself and not just what happens to be in it.
*/
const TABS = [
  { key: 'tracks' as const, label: 'Main Library' },
  { key: 'playlists' as const, label: 'Playlists' },
];

export interface LibraryTabsProps {
  active: LibraryView;
}

export default function LibraryTabs({ active }: LibraryTabsProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <TabBar
      tabs={TABS}
      active={active}
      label="Library views"
      marginBottom={16}
      onSelect={(key) => {
        const to = key === 'tracks' ? paths.library : paths.playlists;
        // Pressing the tab you are already on would otherwise push a duplicate
        // history entry, so Back would appear to do nothing once per press.
        if (to !== pathname) navigate(to);
      }}
    />
  );
}
